import React, { useEffect, useRef, useState, useCallback } from "react";
import { Form, InputGroup, Table, Overlay, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { GetUserLoginLogList } from "../../services/ReduxTookit/Slice/LoginRedux";
import Loader, { LoaderType } from "../../component/common-component/Loader";
import Nodata from "../../component/common-component/Nodata";
import searchicon from "../../assets/images/searchicon.svg";
import filtericon from "../../assets/images/filter.svg";
import useDebounce from "../../hooks/useDebounce";
import { commonservices } from "../../services/CommonServices";
import LoginLogFilterPopup from "./PopUpModels/LoginLogFilterPopup";

const LoginLogManagementModel = () => {
  const dispatch = useDispatch();
  const targetFilter = useRef(null);
  const tableContainerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [loginLogList, setLoginLogList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(1);

  // Dynamic page size based on screen height (fills height + buffer for scrolling)
  const pageSize = Math.max(15, Math.ceil((window.innerHeight - 240) / 40) + 5);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filters, setFilters] = useState({
    logintype: "",
  });
  const [sortStatePayload, setSortStatePayload] = useState({
    field: null,
    direction: null,
  });

  const handleOverlay = (e) => {
    const element = document.getElementById(e?.currentTarget?.id);
    if (element) {
      if (element.classList.contains("overlay-isopen")) {
        element.classList.remove("overlay-isopen");
      } else {
        element.classList.add("overlay-isopen");
      }
    }
  };

  const handleshowFilterPopup = (e) => {
    if (document.getElementById("body")?.classList?.contains("popup-is-open")) {
      document.getElementById("body")?.classList?.remove("popup-is-open");
      handleOverlay(e);
    } else {
      document.getElementById("body")?.classList?.add("popup-is-open");
    }
    setShowFilterPopup(!showFilterPopup);
  };

  const fetchLoginLogs = useCallback(
    async (currentPage, searchText, filterObj, sortObj, isAppend = false) => {
      if (isAppend) {
        setMoreLoading(true);
      } else {
        setLoading(true);
      }

      const body = {
        pagenumber: currentPage,
        pagesize: pageSize,
        searchtext: searchText || "",
        logintype: filterObj?.logintype || "",
        sortfield: sortObj?.field || "",
        sortorder: sortObj?.direction || "",
      };

      try {
        const response = await dispatch(GetUserLoginLogList(body));
        if (response?.payload?.isValidate) {
          const rows = response?.payload?.data?.data || [];
          const count =
            response?.payload?.data?.recordcount ||
            rows?.[0]?.recordcount ||
            rows.length;
          setTotalRecord(count);
          setLoginLogList((prev) => (isAppend ? [...prev, ...rows] : rows));
        } else {
          if (!isAppend) {
            setLoginLogList([]);
            setTotalRecord(0);
          }
        }
      } catch (error) {
        if (!isAppend) {
          setLoginLogList([]);
          setTotalRecord(0);
        }
      } finally {
        setLoading(false);
        setMoreLoading(false);
      }
    },
    [dispatch, pageSize]
  );

  useEffect(() => {
    setPage(1);
    fetchLoginLogs(1, debouncedSearchTerm, filters, sortStatePayload, false);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [debouncedSearchTerm, filters, sortStatePayload, fetchLoginLogs]);

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop - clientHeight < 100 &&
      !loading &&
      !moreLoading &&
      loginLogList.length < totalRecord
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLoginLogs(nextPage, debouncedSearchTerm, filters, sortStatePayload, true);
    }
  };

  const getLoginTypeBadge = (loginType) => {
    const type = String(loginType || "UserName").trim();
    return (
      <span
        style={{
          display: "inline-block",
          padding: "3px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "500",
          backgroundColor: "#e0f2fe",
          color: "#0369a1",
        }}
      >
        {type}
      </span>
    );
  };

  return (
    <>
      <div className="header-search-input searchplaceholder purchaseSearch d-flex justify-content-end align-items-center gap-2">
        <div style={{ width: "280px" }}>
          <InputGroup>
            <InputGroup.Text id="basic-addon1">
              <img src={searchicon} alt="searchicon" className="cursor-pointer" />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by User Name, IP or Device..."
              aria-label="Search"
              aria-describedby="basic-addon1"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        <div className="position-relative d-inline-block">
          <div
            className={`filtering-main ${filters?.logintype || sortStatePayload?.field ? "filter-active" : ""}`}
            id="login-log-filter"
            onClick={(e) => {
              e.preventDefault();
              handleOverlay(e);
              setShowFilterPopup(!showFilterPopup);
              handleshowFilterPopup(e);
            }}
            ref={targetFilter}
          >
            <img src={filtericon} alt="filtericon" />
            <span>Sort & Filter</span>
          </div>

          {showFilterPopup && (
            <Overlay
              target={targetFilter.current}
              show={showFilterPopup}
              placement="bottom"
              rootClose={true}
              onHide={() => setShowFilterPopup(false)}
            >
              <Tooltip id="login-log-overlay">
                <LoginLogFilterPopup
                  sortStatePayload={sortStatePayload}
                  setSortStatePayload={setSortStatePayload}
                  filters={filters}
                  setFilters={setFilters}
                  handleshowPopup={handleshowFilterPopup}
                />
              </Tooltip>
            </Overlay>
          )}
        </div>
      </div>

      <div
        className="table-wrapper-main setting-table-main"
        style={{
          height: "calc(100vh - 140px)",
          minHeight: "calc(100vh - 140px)",
          maxHeight: "calc(100vh - 140px)",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
        }}
      >
        <div
          className="table-responsive"
          onScroll={handleScroll}
          ref={tableContainerRef}
          style={{
            height: "100%",
            maxHeight: "100%",
            overflowY: "auto",
          }}
        >
          <Table>
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 2,
              }}
            >
              <tr>
                <th className="width175">User Name</th>
                <th className="width120">Login Type</th>
                <th className="width150">IP Address</th>
                <th>Device</th>
                <th className="width175 text-end">Login Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <Loader type={LoaderType.TableRow} count={10} nooftd={5} />
              ) : loginLogList.length > 0 ? (
                <>
                  {loginLogList.map((item, index) => (
                    <tr key={item?.userloginlogid ? `${item.userloginlogid}-${index}` : index}>
                      <td className="font-semibold text-start">
                        {item?.displayname || "N/A"}
                      </td>
                      <td>{getLoginTypeBadge(item?.logintype)}</td>
                      <td>{item?.ipaddress || "-"}</td>
                      <td
                        title={item?.device || ""}
                        style={{
                          maxWidth: "280px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item?.device || "-"}
                      </td>
                      <td className="text-end">
                        {item?.logindate
                          ? commonservices.convertDateToLocal(item.logindate)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                  {moreLoading && (
                    <Loader type={LoaderType.TableRow} count={3} nooftd={5} />
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={5}>
                    <Nodata />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </>
  );
};

export default LoginLogManagementModel;
